import { initializeFirestore } from "@firebase/firestore";
import { initializeApp } from "firebase/app";
import {
  browserLocalPersistence,
  browserPopupRedirectResolver,
  GoogleAuthProvider,
  inMemoryPersistence,
  initializeAuth,
  type Auth,
  type Persistence,
} from "firebase/auth";
import {connectFunctionsEmulator, getFunctions, httpsCallable} from "firebase/functions";
import {isBrowserLocalStorageAvailable} from "@/lib/browserStorage";
import {resolveFirebaseAuthDomain} from "@/lib/firebaseAuthDomain";
import "@/lib/installSafeWebStorage";

// The web app's Firebase configuration configured from .env files
const firebaseConfig = {
  apiKey: process.env.NEXT_PUBLIC_FIREBASE_API_KEY,
  // Must stay the configured auth.* helper (e.g. auth.app.braiv.co). Using the
  // app host here makes Google return redirect_uri_mismatch.
  authDomain: resolveFirebaseAuthDomain({
    configuredAuthDomain: process.env.NEXT_PUBLIC_FIREBASE_AUTH_DOMAIN,
  }),
  projectId: process.env.NEXT_PUBLIC_FIREBASE_PROJECT_ID,
  storageBucket: process.env.NEXT_PUBLIC_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.NEXT_PUBLIC_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.NEXT_PUBLIC_FIREBASE_APP_ID,
  measurementId: process.env.NEXT_PUBLIC_FIREBASE_MEASUREMENT_ID,
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);

/**
 * Prefer localStorage over IndexedDB: WebKit IndexedDB deadlocks across tabs and
 * stalls Auth init. When localStorage itself is unavailable (iOS Google in-app
 * browser often exposes `window.localStorage === null`), fall back to in-memory
 * so Firebase never starts BrowserLocalPersistence's poller — that poller throws
 * `this.storage.getItem` TypeErrors every tick (Sentry JAVASCRIPT-NEXTJS-138).
 *
 * NOTE: existing sessions persisted in IndexedDB are not migrated, so users are
 * signed out once after the localStorage switch ships and must re-authenticate.
 */
function isYouTubeOAuthCallbackPath(): boolean {
  if (typeof window === "undefined") return false;
  return window.location.pathname.replace(/\/$/, "") === "/auth/youtube/callback";
}

function resolveAuthPersistence(): Persistence | Persistence[] {
  // YouTube callback never needs a persisted session (it only postMessages
  // the OAuth code). Skip localStorage persistence so iOS Google in-app
  // browsers cannot start BrowserLocalPersistence's poller on a page that
  // often cannot close (Sentry JAVASCRIPT-NEXTJS-138).
  if (isYouTubeOAuthCallbackPath()) {
    return inMemoryPersistence;
  }
  if (isBrowserLocalStorageAvailable()) {
    return [browserLocalPersistence, inMemoryPersistence];
  }
  return inMemoryPersistence;
}

const auth = typeof window === "undefined"
  ? (undefined as unknown as Auth)
  : initializeAuth(app, {
      persistence: resolveAuthPersistence(),
      popupRedirectResolver: browserPopupRedirectResolver,
    });
const firestore = initializeFirestore(app, {
  ignoreUndefinedProperties: true,
});
const provider = new GoogleAuthProvider();

const functions = getFunctions(app, "australia-southeast1");
if (process.env.NODE_ENV == "development") {
  // connectFunctionsEmulator(functions, "localhost", 5001);
}

/**
 * Firebase JS SDK defaults callable HTTP clients to 70s, which is too short for
 * thumbnail + character flows that download images and call external APIs on the server.
 * Matches the 300s timeout used for thumbnail-scoreFix.
 */
export const FIREBASE_CALLABLE_LONG_TIMEOUT_MS = 300_000;

export {
  app,
  auth,
  firestore,
  functions,
  httpsCallable,
  provider,
};
